<!--
  "Ergebnis" — one row per Provisionssatz a run would create. Amounts arrive finished from the
  endpoint; nothing is calculated here and expressly NOTHING is summed (reasoning in the class
  comment). Factor and Berechnungsart are rendered by the module's own output field so the
  simulation speaks the same language as the Beratersicht.
-->
<div class="cp-simulation-entitlements">

    @if (!entitlements?.length) {

        <p-message severity="info"
                   text="Für diese Eingaben entsteht kein Provisionssatz. Warum, sagt das Rechenprotokoll unten.">
        </p-message>

    } @else {

        <div class="overflow-x-auto">

            <p-table [value]="entitlements"
                     dataKey="entitlementRef"
                     [expandedRowKeys]="expandedRows"
                     styleClass="p-datatable-sm">

                <ng-template #header>
                    <tr>
                        <th class="w-3rem"></th>
                        <th>Provisionskomponente</th>
                        <th>Abrechnungskonto</th>
                        <th>Matrix / Zeile</th>
                        <th class="w-8rem">Faktor</th>
                        <th>Haftungsart</th>
                        <th class="w-9rem text-right">Provision</th>
                        <th class="w-9rem text-right">Rücklage</th>
                        <th class="w-9rem text-right">Auszahlbar</th>
                    </tr>
                </ng-template>

                <ng-template #body let-entitlement let-expanded="expanded">
                    <tr>
                        <td>
                            <p-button type="button"
                                      [pRowToggler]="entitlement"
                                      severity="secondary"
                                      size="small"
                                      [text]="true"
                                      [rounded]="true"
                                      [icon]="expanded ? 'fa-solid fa-chevron-down' : 'fa-solid fa-chevron-right'"
                                      pTooltip="Haftungszeitraum und Buchungs-Soll"
                                      tooltipPosition="top">
                            </p-button>
                        </td>

                        <td>
                            <div class="font-semibold">{{ entitlement.componentName }}</div>
                            <div class="text-xs text-color-secondary">
                                Satz {{ entitlement.entitlementRef }}
                                @if (entitlement.calculatedRuleId != null) {
                                    · abgeleitete Regel {{ entitlement.calculatedRuleName ?? entitlement.calculatedRuleId }}
                                }
                            </div>
                        </td>

                        <td>
                            <div>{{ entitlement.provisionAgentName }}</div>
                            <div class="text-xs text-color-secondary">
                                Konto {{ entitlement.provisionAgentId }}
                                @if (roleLabel(entitlement)) {
                                    · {{ roleLabel(entitlement) }}
                                }
                            </div>
                        </td>

                        <td>
                            <div>{{ entitlement.matrixName }}</div>
                            @if (entitlement.matrixRowId != null) {
                                <div class="text-xs text-color-secondary">
                                    Zeile {{ entitlement.matrixRowId }}
                                </div>
                            }
                        </td>

                        <td>
                            <!-- The output field prefers calculationModeInfo and falls back to the value. -->
                            <cp-form-field-output-factor
                                    [value]="entitlement.factor"
                                    [calculationMode]="entitlement.calculationMode"
                                    [calculationModeInfo]="entitlement.calculationModeInfo">
                            </cp-form-field-output-factor>
                        </td>

                        <td>
                            <div>{{ entitlement.liabilityTypeName }}</div>
                            @if (liabilitySourceLabel(entitlement)) {
                                <div class="text-xs text-color-secondary">
                                    {{ liabilitySourceLabel(entitlement) }}
                                </div>
                            }
                        </td>

                        <td class="text-right font-semibold">
                            {{ entitlement.provisionAmount | currency:'EUR':'symbol':'1.2-2' }}
                        </td>

                        <td class="text-right">
                            {{ entitlement.reserveTargetAmount | currency:'EUR':'symbol':'1.2-2' }}
                            <div class="text-xs text-color-secondary">
                                {{ entitlement.reservePercentage | number:'1.2-2' }} %
                            </div>
                        </td>

                        <td class="text-right">
                            {{ entitlement.provisionTargetAmount | currency:'EUR':'symbol':'1.2-2' }}
                        </td>
                    </tr>
                </ng-template>

                <ng-template #expandedrow let-entitlement>
                    <tr>
                        <td colspan="9">
                            <div class="cp-sim-entitlement-detail p-3">

                                <div class="grid">

                                    <!-- Liability period and the state on the settlement key date -->
                                    <div class="col-12 lg:col-6">
                                        <h4 class="text-sm font-semibold mt-0 mb-2">Haftung</h4>

                                        <div class="grid">
                                            <div class="col-6 py-1">
                                                <div class="text-xs text-color-secondary">Haftungsdauer</div>
                                                <div>
                                                    @if (entitlement.liabilityMonths != null) {
                                                        {{ entitlement.liabilityMonths }} Monate
                                                    } @else {
                                                        —
                                                    }
                                                </div>
                                            </div>
                                            <div class="col-6 py-1">
                                                <div class="text-xs text-color-secondary">Status</div>
                                                <div>{{ liabilityStatusLabel(entitlement) || '—' }}</div>
                                            </div>
                                            <div class="col-6 py-1">
                                                <div class="text-xs text-color-secondary">Haftung ab</div>
                                                <div>{{ entitlement.liabilityStartDate | date: 'dd.MM.yyyy' }}</div>
                                            </div>
                                            <div class="col-6 py-1">
                                                <div class="text-xs text-color-secondary">Haftung bis</div>
                                                <div>{{ entitlement.liabilityEndDate | date: 'dd.MM.yyyy' }}</div>
                                            </div>
                                            <div class="col-6 py-1">
                                                <div class="text-xs text-color-secondary">In Haftung</div>
                                                <div>{{ entitlement.inLiabilityAmount | currency:'EUR':'symbol':'1.2-2' }}</div>
                                            </div>
                                            <div class="col-6 py-1">
                                                <div class="text-xs text-color-secondary">Aus der Haftung</div>
                                                <div>{{ entitlement.outOfLiabilityAmount | currency:'EUR':'symbol':'1.2-2' }}</div>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Booking target: what a run would book. Nothing is booked here. -->
                                    <div class="col-12 lg:col-6">
                                        <h4 class="text-sm font-semibold mt-0 mb-2">
                                            Buchungs-Soll
                                            <span class="font-normal text-color-secondary">
                                                — was ein Lauf buchen würde
                                            </span>
                                        </h4>

                                        @if (isWithoutBooking(entitlement)) {
                                            <div class="text-sm text-color-secondary">
                                                Dieser Satz löst keine Buchung aus.
                                            </div>
                                        } @else {
                                            <!-- A nested p-table, like every other list of this
                                                 application; in the normal case it holds one row. -->
                                            <p-table [value]="entitlement.bookings"
                                                     styleClass="p-datatable-sm">

                                                <ng-template #header>
                                                    <tr>
                                                        <th>Ereignis</th>
                                                        <th>Konto</th>
                                                        <th>Buchungsart</th>
                                                        <th class="text-right">Betrag</th>
                                                        <th>Valuta</th>
                                                    </tr>
                                                </ng-template>

                                                <ng-template #body let-booking>
                                                    <tr>
                                                        <td>{{ bookingEventLabel(booking) }}</td>
                                                        <td>{{ accountKindLabel(booking) }}</td>
                                                        <td>{{ booking.transactionTypeName }}</td>
                                                        <td class="text-right">
                                                            {{ booking.amount | currency:'EUR':'symbol':'1.2-2' }}
                                                        </td>
                                                        <td>{{ booking.valutaDate | date: 'dd.MM.yyyy' }}</td>
                                                    </tr>
                                                </ng-template>

                                            </p-table>
                                        }
                                    </div>

                                </div>
                            </div>
                        </td>
                    </tr>
                </ng-template>

            </p-table>
        </div>

    }

</div>
